import { Inject, Injectable, InjectionToken } from '@angular/core';
import { FILING_SUB_TABS, FilingMode, FilingSubTabItem } from '@fsx/fsx-shared';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  merge,
  shareReplay,
  tap,
  withLatestFrom,
} from 'rxjs';
import {
  FsxFilingDataService,
  IFilingDataService,
} from '../services/filing-data.service';
import {
  FsxFilingSubTabsFactoryService,
  IFilingSubTabsFactoryService,
} from './filing-sub-tabs-factory.service';
import {
  FsxValidationIndicatorService,
  IValidationIndicatorService,
} from '../services/validation-indicator.service';

export const FsxFilingSubTabsService =
  new InjectionToken<IFilingSubTabsService>('FsxFilingSubTabsService');

export interface IFilingSubTabsService {
  filingSubTabItems$: Observable<FilingSubTabItem[]>;
  activeSubTabItem$: Observable<FilingSubTabItem>;
  activateFilingSubTabItem(filingSubTabItem: FilingSubTabItem): void;
  activateNext(): void;
  activatePrevious(): void;
}

@Injectable()
export class FilingSubTabsService implements IFilingSubTabsService {
  private filingSubTabItemsCache$$ = new BehaviorSubject<FilingSubTabItem[]>(
    []
  );
  private filingSubTabItemsCache$ = this.filingSubTabItemsCache$$
    .asObservable()
    .pipe(
      map((filingSubTabItems: FilingSubTabItem[]) => {
        const filingSubTabItemsCopy = filingSubTabItems.map((x) =>
          Object.assign({}, x)
        ); // Ensures immutability
        return filingSubTabItemsCopy;
      })
    );

  private filingSubTabItemsForFilingMode$: Observable<FilingSubTabItem[]> =
    this.filingDataService.filingMode$.pipe(
      filter((filingMode: FilingMode) => {
        const availableFilingModes = [
          FilingMode.OriginalPetition,
          FilingMode.Subsequent,
        ];
        return availableFilingModes.includes(filingMode);
      }),
      map((filingMode: FilingMode) => {
        switch (filingMode) {
          case FilingMode.OriginalPetition:
            return this.filingSubTabsFactoryService.getFilingSubTabItemsForOpf();
          case FilingMode.Subsequent:
            return this.filingSubTabsFactoryService.getFilingSubTabItemsForSubF();
          default:
            return [];
        }
      })
    );

  private activateFilingSubTabItem$$ = new Subject<FilingSubTabItem>();
  private filingSubTabItemsNewTabActivated$: Observable<FilingSubTabItem[]> =
    this.activateFilingSubTabItem$$.pipe(
      withLatestFrom(this.filingSubTabItemsCache$),
      map(
        ([activateFilingSubTabItem, filingSubTabItemsCache]: [
          FilingSubTabItem,
          FilingSubTabItem[]
        ]) => {
          return filingSubTabItemsCache.map((tab: FilingSubTabItem) => {
            tab.isActive = tab.tabIndex === activateFilingSubTabItem.tabIndex;
            return tab;
          });
        }
      )
    );

  private activateNextFilingSubTabItem$$ = new Subject<void>();
  private filingSubTabItemsNextTabActivated$: Observable<FilingSubTabItem[]> =
    this.activateNextFilingSubTabItem$$.pipe(
      withLatestFrom(this.filingSubTabItemsCache$),
      map(([_, filingSubTabItemsCache]: [void, FilingSubTabItem[]]) => {
        const currentSelectedTab: FilingSubTabItem =
          filingSubTabItemsCache.find((tab) => tab.isActive)!;
        const newSelectedTab: FilingSubTabItem | undefined =
          filingSubTabItemsCache.find(
            (tab) => tab.tabIndex === currentSelectedTab.tabIndex + 1
          );
        const newSelectedTabIndex: number =
          newSelectedTab?.tabIndex || currentSelectedTab.tabIndex;
        return filingSubTabItemsCache.map((tab: FilingSubTabItem) => {
          tab.isActive = tab.tabIndex === newSelectedTabIndex;
          return tab;
        });
      })
    );

  private activatePreviousFilingSubTabItem$$ = new Subject<void>();
  private filingSubTabItemsPreviousTabActivated$: Observable<
    FilingSubTabItem[]
  > = this.activatePreviousFilingSubTabItem$$.pipe(
    withLatestFrom(this.filingSubTabItemsCache$),
    map(([_, filingSubTabItemsCache]: [void, FilingSubTabItem[]]) => {
      const currentSelectedTab: FilingSubTabItem = filingSubTabItemsCache.find(
        (tab) => tab.isActive
      )!;
      const newSelectedTab: FilingSubTabItem | undefined =
        filingSubTabItemsCache.find(
          (tab) => tab.tabIndex === currentSelectedTab.tabIndex - 1
        );
      const newSelectedTabIndex: number = newSelectedTab
        ? newSelectedTab.tabIndex
        : currentSelectedTab.tabIndex;
      return filingSubTabItemsCache.map((tab: FilingSubTabItem) => {
        tab.isActive = tab.tabIndex === newSelectedTabIndex;
        return tab;
      });
    })
  );

  private filingSubTabItemsValidated$: Observable<FilingSubTabItem[]> =
    combineLatest([
      this.validationIndicatorService.isValidCaseDetails$,
      this.validationIndicatorService.isValidPartiesAndRepresentation$,
      this.validationIndicatorService.isValidDocuments$,
      this.validationIndicatorService.isValidReview$,
    ]).pipe(
      withLatestFrom(this.filingSubTabItemsCache$),
      filter(
        ([
          [_isDetailsValid, _isPartiesValid, _isDocumentsValid, _isReviewValid],
          filingSubTabItems,
        ]: [[boolean, boolean, boolean, boolean], FilingSubTabItem[]]) => {
          return filingSubTabItems && filingSubTabItems.length > 0;
        }
      ),
      map(
        ([
          [isDetailsValid, isPartiesValid, isDocumentsValid, isReviewValid],
          filingSubTabItems,
        ]: [[boolean, boolean, boolean, boolean], FilingSubTabItem[]]) => {
          return filingSubTabItems.reduce(
            (acc: FilingSubTabItem[], cur: FilingSubTabItem) => {
              // NOTE: Strict checks are necessary to protect against undefined values.
              // - when undefined the tabs are considered valid (no validation has taken place)
              if (cur.name === FILING_SUB_TABS.DETAILS) {
                cur.isValid = isDetailsValid === false ? false : true;
              }
              if (cur.name === FILING_SUB_TABS.PARTIES) {
                cur.isValid = isPartiesValid === false ? false : true;
              }
              if (cur.name === FILING_SUB_TABS.DOCUMENTS) {
                cur.isValid = isDocumentsValid === false ? false : true;
              }
              if (cur.name === FILING_SUB_TABS.REVIEW) {
                cur.isValid = isReviewValid === false ? false : true;
              }
              acc.push(cur);
              return acc;
            },
            []
          );
        }
      )
    );

  filingSubTabItems$: Observable<FilingSubTabItem[]> = merge(
    this.filingSubTabItemsForFilingMode$,
    this.filingSubTabItemsNewTabActivated$,
    this.filingSubTabItemsNextTabActivated$,
    this.filingSubTabItemsPreviousTabActivated$,
    this.filingSubTabItemsValidated$
  ).pipe(
    tap((filingSubTabItems: FilingSubTabItem[]) => {
      this.filingSubTabItemsCache$$.next(filingSubTabItems);
    }),
    distinctUntilChanged(
      (
        filingSubTabItems1: FilingSubTabItem[],
        filingSubTabItems2: FilingSubTabItem[]
      ) => {
        return (
          JSON.stringify(filingSubTabItems1) ===
          JSON.stringify(filingSubTabItems2)
        );
      }
    ),
    shareReplay(1) // TODO: Add tests to check that multiple late subscribers get the same data
  );

  activeSubTabItem$: Observable<FilingSubTabItem> =
    this.filingSubTabItems$.pipe(
      map((filingSubTabItems: FilingSubTabItem[]) => {
        const activeSubTabItem: FilingSubTabItem = filingSubTabItems.find(
          (tab: FilingSubTabItem) => tab.isActive
        )!;
        return activeSubTabItem;
      }),
      distinctUntilChanged(
        (subTabItem1: FilingSubTabItem, subTabItem2: FilingSubTabItem) => {
          return JSON.stringify(subTabItem1) === JSON.stringify(subTabItem2);
        }
      )
    );

  constructor(
    @Inject(FsxFilingDataService)
    private readonly filingDataService: IFilingDataService,
    @Inject(FsxValidationIndicatorService)
    private readonly validationIndicatorService: IValidationIndicatorService,
    @Inject(FsxFilingSubTabsFactoryService)
    private readonly filingSubTabsFactoryService: IFilingSubTabsFactoryService
  ) {}

  activateFilingSubTabItem(filingSubTabItem: FilingSubTabItem): void {
    this.activateFilingSubTabItem$$.next(filingSubTabItem);
  }

  activateNext(): void {
    this.activateNextFilingSubTabItem$$.next();
  }

  activatePrevious(): void {
    this.activatePreviousFilingSubTabItem$$.next();
  }
}
